import React, { useEffect, useState } from "react";
import { Badge, Dropdown, DropdownMenu, DropdownToggle } from "reactstrap";

import { SearchMatches } from "./components/SearchMatches";

/** Which populations a search covers. Both start on; each box is literal. */
export interface DocumentSearchScopes {
  slackWorkspace: boolean;
  platformTeam: boolean;
}

export interface DocumentSearchParams {
  search: string;
  showDeleted: boolean;
  scopes?: DocumentSearchScopes;
}

type DocumentSearchProps = {
  typeName: string;
  placeholder: string;
  omitDeleted?: boolean;
  /** Opt-in: only the Users page searches across two populations. */
  scopeToggles?: boolean;
  loading: boolean;
  numFound?: number;
  onSubmit: (searchParams: DocumentSearchParams) => void;
};

export const DocumentSearch: React.FunctionComponent<DocumentSearchProps> = (
  props: DocumentSearchProps,
) => {
  const [search, setSearch] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [scopes, setScopes] = useState<DocumentSearchScopes>({
    slackWorkspace: true,
    platformTeam: true,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  // The Enterprise page renders two of these, so a fixed id would bind both
  // pages' labels to the first input. React 17 has no useId, so mint one per
  // instance and keep it for the component's life.
  const [uid] = useState(() => `ds${Math.random().toString(36).slice(2, 9)}`);
  const { onSubmit } = props;

  // Filters live behind a button now, so a non-default one has to announce
  // itself — otherwise a forgotten toggle reads as missing data.
  const activeFilterCount =
    (showDeleted ? 1 : 0) +
    (scopes.slackWorkspace ? 0 : 1) +
    (scopes.platformTeam ? 0 : 1);
  useEffect(() => {
    onSubmit({ search, showDeleted, scopes });
  }, [search, showDeleted, scopes, onSubmit]);

  return (
    <>
      <div className="row">
        <div className="col">
          <div className="input-group">
            <div className="input-group-prepend">
              <span className="input-group-text">
                <i className="fas fa-search" />
              </span>
            </div>{" "}
            <input
              className="form-control"
              type="text"
              placeholder={props.placeholder}
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
              }}
            />
          </div>
        </div>
        {!props.scopeToggles && !props.omitDeleted && (
          <div className="col">
            <div className="form-check pt-2">
              <input
                type="checkbox"
                className="form-check-input"
                id={`showDeleted-${uid}`}
                onChange={(e) => setShowDeleted(e.target.checked)}
              />
              <label
                className="form-check-label"
                htmlFor={`showDeleted-${uid}`}
              >
                Show Deleted
              </label>
            </div>
          </div>
        )}
        {props.scopeToggles && (
          <div className="col">
            <Dropdown
              isOpen={filtersOpen}
              toggle={() => setFiltersOpen((open) => !open)}
            >
              <DropdownToggle caret color="secondary" outline>
                Filters
                {activeFilterCount > 0 && (
                  <Badge color="info" pill className="ml-2">
                    {activeFilterCount}
                  </Badge>
                )}
              </DropdownToggle>
              <DropdownMenu>
                {/* Clicks inside the menu would otherwise reach reactstrap's
                    document listener and close it after a single tick. */}
                <div className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  {!props.omitDeleted && (
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`showDeleted-${uid}`}
                        checked={showDeleted}
                        onChange={(e) => setShowDeleted(e.target.checked)}
                      />
                      <label
                        className="form-check-label text-nowrap"
                        htmlFor={`showDeleted-${uid}`}
                      >
                        Show Deleted
                      </label>
                    </div>
                  )}
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`scopeSlackWorkspace-${uid}`}
                      checked={scopes.slackWorkspace}
                      onChange={(e) =>
                        setScopes((prev) => ({
                          ...prev,
                          slackWorkspace: e.target.checked,
                        }))
                      }
                    />
                    <label
                      className="form-check-label text-nowrap"
                      htmlFor={`scopeSlackWorkspace-${uid}`}
                    >
                      Slack Workspace
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`scopePlatformTeam-${uid}`}
                      checked={scopes.platformTeam}
                      onChange={(e) =>
                        setScopes((prev) => ({
                          ...prev,
                          platformTeam: e.target.checked,
                        }))
                      }
                    />
                    <label
                      className="form-check-label text-nowrap"
                      htmlFor={`scopePlatformTeam-${uid}`}
                    >
                      Platform Team
                    </label>
                  </div>
                </div>
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
      </div>
      <div className="row">
        <div className="col">
          <div className="my-2">
            {!search ? (
              <></>
            ) : props.loading ? (
              <>
                <div
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                />
                Loading...
              </>
            ) : props.numFound === undefined ? (
              <></>
            ) : (
              <SearchMatches
                numFound={props.numFound}
                typeName={props.typeName}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
